import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export default function Register() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, firstName);
      toast.success("Welcome to Bitcoin Brain!");
      navigate("/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${MASCOT_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#050505] to-transparent" />
        <div className="absolute inset-0 bg-[#050505]/50" />
        <div className="relative z-10 flex items-end p-12">
          <div className="max-w-md">
            <p className="text-[#F7931A] font-heading text-2xl font-bold">
              "Learn Bitcoin. Own Bitcoin."
            </p>
            <p className="text-zinc-400 mt-2">
              Join thousands of working families learning the truth about money.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-10 h-10 rounded-lg" />
            <span className="font-heading text-xl font-bold text-white">
              BITCOIN <span className="text-[#F7931A]">BRAIN</span>
            </span>
          </Link>

          <h1 className="font-heading text-4xl font-bold text-white mb-2">
            START YOUR <span className="text-[#F7931A]">JOURNEY</span>
          </h1>
          <p className="text-zinc-400 mb-8">
            Create your free account and earn your first 210 BTC XP
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-zinc-300">First Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="pl-10 bg-black/50 border-white/10 text-white h-12"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-black/50 border-white/10 text-white h-12"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-black/50 border-white/10 text-white h-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-black/50 border-white/10 text-white h-12"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            {/* Terms checkbox */}
            <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#F7931A] cursor-pointer flex-shrink-0"
                data-testid="terms-checkbox"
              />
              <span className="text-zinc-500 text-xs leading-relaxed">
                I agree to the{" "}
                <Link to="/legal/terms" className="text-[#F7931A] hover:underline" target="_blank">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/legal/privacy" className="text-[#F7931A] hover:underline" target="_blank">Privacy Policy</Link>
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-lg"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create Free Account <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </form>

          <p className="text-zinc-600 text-xs text-center mt-4" data-testid="register-disclaimer">
            Bitcoin Brain is not a financial advisor. Content is for educational, evangelical, and entertainment purposes only. Nothing here constitutes financial advice. By signing up, you agree to take responsibility for your own financial decisions.
          </p>

          <p className="text-zinc-500 text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#F7931A] hover:underline" data-testid="login-link">
              Log in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
